import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'

// Document Interfaces
export interface DocumentFolder {
  /** Stable id. Absent only on folders created before ids existed. */
  id?: string
  name: string
  /** Id of the containing folder; absent for a top-level folder (STFFLG-4204). */
  parentId?: string
  /** Set on a candidate's per-EOR OlliBoard folder. */
  eorId?: string
  files: DocumentFile[]
  isSystemFolder?: boolean // True if folder is system-created and cannot be renamed/deleted
}

export interface DocumentFile {
  _id: string
  fileName: string
  fileSize: number
  uploadDate: Date
  isPrivate: boolean
  uploadedBy: string
  uploadedByFirstName?: string
  uploadedByLastName?: string
}

export interface DocumentResponse {
  _id: string
  entityId: string
  entityType: string
  uploadedBy: string
  uploadedAt: Date
  folders: DocumentFolder[]
}

/*
 * Folder addressing (STFFLG-4204). Where a request takes both, the API resolves
 * `…FolderId` first; a `…FolderName` alone always means a TOP-LEVEL folder. Sending the
 * name alongside the id keeps a request meaningful to an API build that predates ids.
 */

export interface CreateFolderRequest {
  documentId: string
  name: string
  /** Create inside this folder; omit for a top-level folder. */
  parentFolderId?: string
}

export interface RenameFolderRequest {
  documentId: string
  currentFolderName?: string
  folderId?: string
  newFolderName: string
}

export interface UploadFileRequest {
  documentId: string
  folderName: string
  folderId?: string
  isPrivate: boolean
  file: File
}

export interface DeleteFileRequest {
  documentId: string
  folderName?: string
  folderId?: string
  fileId: string
}

export interface DownloadFileRequest {
  documentId: string
  folderName?: string
  folderId?: string
  fileId: string
}

export interface ArchiveFileRequest {
  documentId: string
  folderName?: string
  folderId?: string
  fileId: string
}

export interface MoveFileRequest {
  documentId: string
  fileId: string
  sourceFolderName?: string
  sourceFolderId?: string
  destinationFolderName?: string
  destinationFolderId?: string
}

/**
 * Query parameters for the file operations, sent through axios `params` so every value
 * is URL-encoded. They used to be interpolated into the URL raw, which broke on a folder
 * name containing `&`, `#`, `?` or `+`. Undefined values are dropped by axios.
 */
function fileQueryParams(
  request: {documentId: string; folderName?: string; folderId?: string; fileId: string},
  agencyId?: string,
) {
  return {
    documentId: request.documentId,
    folderName: request.folderName,
    folderId: request.folderId,
    fileId: request.fileId,
    ...(agencyId && {agencyId}),
  }
}

/**
 * Document API Service
 * Handles all document-related operations (folders, files, uploads, downloads)
 */
export class DocumentApi extends BaseApi {
  /**
   * Get document by entity ID
   */
  async getDocumentByEntityId(entityId: string, agencyId?: string): Promise<DocumentResponse> {
    try {
      const params = agencyId ? {agencyId} : {}
      const response = await this.get<DocumentResponse>(
        `/document/getDocumentByEntityId/${entityId}`,
        {params},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching document by entity ID:', error)
      throw error
    }
  }

  /**
   * Seed a document entity with default folders.
   * Creates one if it doesn't exist; returns the existing one otherwise.
   */
  async createDefaultFolders(
    entityId: string,
    entityType: string,
    agencyId?: string,
  ): Promise<DocumentResponse> {
    try {
      const params = agencyId ? {agencyId} : {}
      const response = await this.post<DocumentResponse>(
        '/document/createDefaultFolders',
        {entityId, entityType},
        {params},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error seeding document entity:', error)
      throw error
    }
  }

  /**
   * Create a new folder in a document
   */
  async createFolder(request: CreateFolderRequest, agencyId?: string): Promise<DocumentResponse> {
    try {
      const params = agencyId ? {agencyId} : {}
      const response = await this.put<DocumentResponse>('/document/createFolder', request, {
        params,
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating folder:', error)
      throw error
    }
  }

  /**
   * Rename an existing folder
   */
  async renameFolder(request: RenameFolderRequest, agencyId?: string): Promise<DocumentResponse> {
    try {
      const params = agencyId ? {agencyId} : {}
      const response = await this.put<DocumentResponse>('/document/renameFolder', request, {
        params,
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error renaming folder:', error)
      throw error
    }
  }

  /**
   * Upload a file to a folder
   * Uses FormData for multipart/form-data uploads
   */
  async uploadFile(request: UploadFileRequest, agencyId?: string): Promise<DocumentResponse> {
    try {
      const formData = new FormData()
      formData.append('file', request.file)

      const params = {
        ...(agencyId && {agencyId}),
        documentId: request.documentId,
        folderName: request.folderName,
        // Overrides the path's folder name, and is the only way to reach a subfolder.
        folderId: request.folderId,
        isPrivate: request.isPrivate,
      }

      // Encoded: the folder name is a path segment, and used to be sent raw.
      const response = await this.put<DocumentResponse>(
        `/document/uploadFile/${encodeURIComponent(request.documentId)}/${encodeURIComponent(
          request.folderName,
        )}`,
        formData,
        {
          params,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error uploading file:', error)
      throw error
    }
  }

  /**
   * Delete a file from a folder
   */
  async deleteFile(request: DeleteFileRequest, agencyId?: string): Promise<DocumentResponse> {
    try {
      const response = await this.put<DocumentResponse>('/document/deleteFile', undefined, {
        params: fileQueryParams(request, agencyId),
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error deleting file:', error)
      throw error
    }
  }

  /**
   * Archive a file (soft delete)
   */
  async archiveFile(request: ArchiveFileRequest, agencyId?: string): Promise<DocumentResponse> {
    try {
      const response = await this.put<DocumentResponse>('/document/archiveFile', undefined, {
        params: fileQueryParams(request, agencyId),
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error archiving file:', error)
      throw error
    }
  }

  /**
   * Unarchive a previously archived file
   */
  async unarchiveFile(request: ArchiveFileRequest, agencyId?: string): Promise<DocumentResponse> {
    try {
      const response = await this.put<DocumentResponse>('/document/unarchiveFile', undefined, {
        params: fileQueryParams(request, agencyId),
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error unarchiving file:', error)
      throw error
    }
  }

  /**
   * Move a file from one folder to another
   */
  async moveFile(request: MoveFileRequest, agencyId?: string): Promise<DocumentResponse> {
    try {
      const response = await this.put<DocumentResponse>('/document/moveFile', undefined, {
        params: {
          documentId: request.documentId,
          fileId: request.fileId,
          sourceFolderName: request.sourceFolderName,
          sourceFolderId: request.sourceFolderId,
          destinationFolderName: request.destinationFolderName,
          destinationFolderId: request.destinationFolderId,
          ...(agencyId && {agencyId}),
        },
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error moving file:', error)
      throw error
    }
  }

  /**
   * Download a file from a folder
   * Returns a Blob for downloading
   */
  async downloadFile(request: DownloadFileRequest, agencyId?: string): Promise<Blob> {
    try {
      const response = await this.get<Blob>('/document/downloadFile', {
        params: fileQueryParams(request, agencyId),
        responseType: 'blob',
      })

      return new Blob([response.data])
    } catch (error: unknown) {
      reportCatchError('Error downloading file:', error)
      throw error
    }
  }
}

// Export singleton instance
export const documentApi = new DocumentApi()
